import React, { useEffect, useState } from "react";
import { adminApi } from "@/api/adminOneClient";
import ComplianceLock from "@/components/compliance/ComplianceLock";

const SEV_COLOR = { LOW: "#7AC860", MEDIUM: "#F5C842", HIGH: "#E8A020", EXTREME: "#C84A3A", QUIET: "#7A6A4A", OPEN: "#60B8E8" };

export default function AdminWatchtowers() {
  const [overview, setOverview] = useState({ watchtowers: [] });
  const [active, setActive] = useState(null);
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);

  useEffect(() => {
    adminApi.watchtowersSummary().then(({ data }) => {
      setOverview(data || { watchtowers: [] });
      const first = data?.watchtowers?.[0];
      if (first) loadEvents(first.watchtower);
    }).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const loadEvents = async (wt) => {
    setActive(wt);
    setLoadingEvents(true);
    try {
      const { data } = await adminApi.watchtowerEvents(wt, 25);
      setEvents(data?.events || []);
    } finally { setLoadingEvents(false); }
  };

  return (
    <div className="admin-page mesh-shell" data-testid="admin-watchtowers">
      <div className="mesh-bg-radar" aria-hidden="true" />
      <div className="mesh-bg-particles" aria-hidden="true" />
      {/* Phase 26C — Mandated Compliance Lock™ banner */}
      <ComplianceLock variant="banner" testid="admin-watchtowers-compliance" />
      <div className="admin-page__head">
        <h2 className="admin-page__title">Global Watchtowers</h2>
        <div className="mesh-eyebrow">5 panels · threat/opportunity radar</div>
      </div>

      <section className="mesh-watchtowers">
        {overview.watchtowers.map((w) => {
          const color = SEV_COLOR[w.status] || SEV_COLOR.OPEN;
          const isActive = active === w.watchtower;
          return (
            <article
              key={w.watchtower}
              className={`mesh-tower ${isActive ? "is-active" : ""}`}
              style={{ borderColor: color }}
              data-testid={`watchtower-${w.watchtower}`}
              onClick={() => loadEvents(w.watchtower)}
            >
              <div className="mesh-tower__radar" style={{ color }}>
                <span className="mesh-radar-ring" />
                <span className="mesh-radar-ring mesh-radar-ring--2" />
                <span className="mesh-radar-ring mesh-radar-ring--3" />
                <span className="mesh-radar-sweep" />
                <span className="mesh-radar-core" />
              </div>
              <h3 className="mesh-tower__title">{w.label}</h3>
              <div className="mesh-tower__status" style={{ color }}>{w.status}</div>
              <div className="mesh-tower__metrics">
                <span><b>{w.total}</b> total</span>
                <span><b>{w.open}</b> open</span>
                <span style={{ color: SEV_COLOR.HIGH }}><b>{w.high}</b> high</span>
                <span style={{ color: SEV_COLOR.EXTREME }}><b>{w.extreme}</b> extreme</span>
              </div>
              {w.top_signal && (
                <div className="mesh-tower__top">
                  <div className="mesh-tower__top-label">TOP SIGNAL</div>
                  <div className="mesh-tower__top-title">{w.top_signal.title}</div>
                  <div className="mesh-tower__top-meta">
                    {w.top_signal.asset && <span className="mesh-pill mesh-pill--gold">{w.top_signal.asset}</span>}
                    <span className="mesh-pill" style={{ borderColor: SEV_COLOR[w.top_signal.severity], color: SEV_COLOR[w.top_signal.severity] }}>{w.top_signal.severity}</span>
                    <span className="mesh-pill mesh-pill--ghost">Score {w.top_signal.oracle_score}</span>
                  </div>
                </div>
              )}
            </article>
          );
        })}
      </section>

      <section className="admin-section mesh-section" style={{ marginTop: 14 }}>
        <h3 className="admin-section__title">
          {active ? `Live Events · ${active.toUpperCase()}` : "Select a watchtower to view events"}
        </h3>
        {loadingEvents ? <div className="admin-saved">Loading…</div> : (
          <ol className="mesh-timeline">
            {events.map((e) => (
              <li key={e.id} className="mesh-timeline__item" style={{ borderColor: SEV_COLOR[e.severity] }} data-testid={`tower-event-${e.id}`}>
                <span className="mesh-timeline__time">{new Date(e.created_at).toLocaleTimeString()}</span>
                <span className="mesh-pill" style={{ borderColor: SEV_COLOR[e.severity], color: SEV_COLOR[e.severity] }}>{e.severity}</span>
                {e.asset && <span className="mesh-pill mesh-pill--gold">{e.asset}</span>}
                <span className="mesh-pill mesh-pill--ghost">{e.category}</span>
                <span className="mesh-timeline__title">{e.title}</span>
                <span className="mesh-score mesh-score--inline"><span className="mesh-score__num">{e.oracle_score}</span></span>
              </li>
            ))}
            {events.length === 0 && active && <div className="admin-foot-note">No events.</div>}
          </ol>
        )}
      </section>
    </div>
  );
}
